// apps/web/src/features/dashboards/china-requests/queries.ts (#1589)
// React Query key factory + hook for the leadership China-requests
// dashboard.
//
// Cache policy mirrors `finance/revenue/queries.ts` and the backend's
// 5-minute in-memory cache: `staleTime: 5*60_000` reuses the payload when
// navigating away and back inside the same window; `refetchOnWindowFocus`
// and `refetchInterval` are off so visible leadership numbers never shift
// without an explicit user action.
import { useQuery } from '@tanstack/react-query';
import type {
  ChinaRequestsDashboardSummary,
  ChinaRequestsDashboardTrend,
  ChinaRequestsSummaryQuery,
} from '@magimp/shared';
import { fetchChinaRequestsDashboard, fetchChinaRequestsTrend } from './api.js';

export const chinaRequestsDashboardKeys = {
  all: ['china-requests-dashboard'] as const,
  // WHY: the key carries every resolved period param (preset + custom
  //   bounds) so changing the period selector or a custom date refetches.
  summary: (query: ChinaRequestsSummaryQuery) =>
    [
      'china-requests-dashboard',
      'summary',
      query.period,
      query.from ?? null,
      query.to ?? null,
    ] as const,
  trend: (query: ChinaRequestsSummaryQuery) =>
    [
      'china-requests-dashboard',
      'trend',
      query.period,
      query.from ?? null,
      query.to ?? null,
    ] as const,
};

const STALE_MS = 5 * 60_000;

export function useChinaRequestsDashboard(query: ChinaRequestsSummaryQuery | null) {
  return useQuery<ChinaRequestsDashboardSummary>({
    queryKey:
      query !== null
        ? chinaRequestsDashboardKeys.summary(query)
        : ([...chinaRequestsDashboardKeys.all, 'summary', 'disabled'] as const),
    queryFn: ({ signal }) => {
      if (query === null) throw new Error('query disabled');
      return fetchChinaRequestsDashboard(query, { signal });
    },
    enabled: query !== null,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
}

// Lazily-loaded trend series. Same cache policy as the summary hook; it is a
// SEPARATE request so the chart streams in after the headline numbers render.
// The caller only mounts the chart (and thus this hook) once a valid period
// exists and the summary has succeeded.
export function useChinaRequestsTrend(query: ChinaRequestsSummaryQuery) {
  return useQuery<ChinaRequestsDashboardTrend>({
    queryKey: chinaRequestsDashboardKeys.trend(query),
    queryFn: ({ signal }) => fetchChinaRequestsTrend(query, { signal }),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
}
